import { PDFDocument } from 'pdf-lib'

type ImageSource = string | Blob | File | ArrayBuffer

/**
 * Normalizes an image by drawing it through canvas, which applies EXIF orientation.
 * Returns JPEG bytes with correct orientation baked into pixel data.
 */
async function normalizeImage(
  source: ImageSource,
): Promise<{ bytes: ArrayBuffer; width: number; height: number }> {
  let blob: Blob
  if (source instanceof Blob || source instanceof File) {
    blob = source
  } else if (source instanceof ArrayBuffer) {
    blob = new Blob([source])
  } else {
    const response = await fetch(source)
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${source} (Status: ${response.status})`)
    }
    blob = await response.blob()
  }

  const bitmap = await createImageBitmap(blob)
  const { width, height } = bitmap

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  const outputBlob = await new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.92),
  )
  const bytes = await outputBlob.arrayBuffer()

  return { bytes, width, height }
}

/**
 * Converts array of image URLs, Blobs, or Files to a single PDF file
 * Each image becomes one page in the PDF
 *
 * @param imageSources - Array of image URLs, Blobs, Files, or ArrayBuffers
 * @param pdfFileName - Name for the generated PDF file (default: 'carousel.pdf')
 * @returns PDF File object ready to upload
 */
export async function convertImagesToPdf(
  imageSources: ImageSource[],
  pdfFileName: string = 'carousel.pdf',
): Promise<File> {
  try {
    const pdfDoc = await PDFDocument.create()

    // Normalize all images first (applies EXIF orientation via canvas)
    const normalizedImages = await Promise.all(imageSources.map(normalizeImage))

    // Determine page dimensions based on first image aspect ratio
    const pageWidth = 1080
    let pageHeight = 1080

    if (normalizedImages.length > 0) {
      const first = normalizedImages[0]!
      const aspectRatio = first.width / first.height

      console.log('[PDF] First image aspect ratio:', aspectRatio.toFixed(3), {
        width: first.width,
        height: first.height,
      })

      // If aspect ratio is closer to 4:5 (0.8) than 1:1 (1.0), use 4:5 format
      if (aspectRatio < 0.9) {
        pageHeight = 1350
        console.log('[PDF] Using 4:5 format (1080x1350)')
      } else {
        console.log('[PDF] Using 1:1 format (1080x1080)')
      }
    }

    for (const normalized of normalizedImages) {
      // All normalized images are JPEG after canvas export
      const image = await pdfDoc.embedJpg(normalized.bytes)

      const page = pdfDoc.addPage([pageWidth, pageHeight])

      const imageDims = image.scale(1)

      // Calculate scaling to FILL page (no white borders - may crop image)
      const widthRatio = pageWidth / imageDims.width
      const heightRatio = pageHeight / imageDims.height
      const scale = Math.max(widthRatio, heightRatio) // Use max to fill, not fit

      const scaledWidth = imageDims.width * scale
      const scaledHeight = imageDims.height * scale

      // Center image on page (will crop edges if needed to fill)
      const x = (pageWidth - scaledWidth) / 2
      const y = (pageHeight - scaledHeight) / 2

      // Draw image on page
      page.drawImage(image, {
        x,
        y,
        width: scaledWidth,
        height: scaledHeight,
      })
    }

    // Generate PDF as bytes
    const pdfBytes = await pdfDoc.save()
    const normalizedBytes = new Uint8Array(pdfBytes)

    // Convert to File object
    const pdfFile = new File([normalizedBytes], pdfFileName, { type: 'application/pdf' })

    console.log('[PDF] PDF created successfully!')
    console.log('[PDF] Size:', `${(normalizedBytes.length / 1024).toFixed(2)} KB`)
    console.log('[PDF] Image count:', imageSources.length)

    return pdfFile
  } catch (error) {
    console.error('Error converting images to PDF:', error)
    throw new Error('Failed to convert images to PDF')
  }
}
